"use client";

import { FilterOptions } from "@/@types";
import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { ROUTES } from "@/config/routes";
import Image from "next/image";
import { useFilterAutoScroll } from "@/hooks/useFilterAutoScroll";

type Props = {
  filters: FilterOptions;
  unitType: "metric" | "english";
  setUnitType: (type: "metric" | "english") => void;
  selectedIndustry?: string;
  selectedFamily?: string;
  selectedSubfamily?: string;
  selectedBrand?: string;
  isMenuOpen?: boolean;
  toggleMenu?: () => void;
};

export default function ProductsFiltersCard({
  filters,
  unitType,
  setUnitType,
  selectedIndustry = "",
  selectedFamily = "",
  selectedSubfamily = "",
  selectedBrand = "",
  isMenuOpen = false,
  toggleMenu = () => {},
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const firstSegment = pathname.split("/")[1];

  const [industry, setIndustry] = useState<string>(selectedIndustry);
  const [family, setFamily] = useState<string>(selectedFamily);
  const [subfamily, setSubfamily] = useState<string>(selectedSubfamily);
  const [brand, setBrand] = useState<string>(selectedBrand);

  const [isMobile, setIsMobile] = useState(false);
  const [showMenuIcon, setShowMenuIcon] = useState(false);

  const industriesContainerRef = useRef<HTMLDivElement>(null);
  const familiesContainerRef = useRef<HTMLDivElement>(null);
  const subfamiliesContainerRef = useRef<HTMLDivElement>(null);
  const brandsContainerRef = useRef<HTMLDivElement>(null);

  const navigateToFilters = useCallback(
    (
      industry?: string,
      family?: string,
      subFamily?: string,
      brand?: string,
    ) => {
      if (firstSegment === "productos-por-marca" && brand) {
        if (subFamily && industry && family) {
          return router.replace(
            ROUTES.brands.subfamily(brand, industry, family, subFamily),
          );
        }

        if (industry && family) {
          return router.replace(ROUTES.brands.family(brand, industry, family));
        }

        if (industry) {
          return router.replace(ROUTES.brands.industry(brand, industry));
        }

        return router.replace(ROUTES.brands.brand(brand));
      }

      if (brand)
        return router.replace(
          ROUTES.products.brands(industry, family, subFamily, brand),
        );
      if (subFamily)
        return router.replace(
          ROUTES.products.subfamily(industry, family, subFamily),
        );
      if (family)
        return router.replace(ROUTES.products.family(industry, family));
      if (industry) return router.replace(ROUTES.products.industry(industry));
      router.replace(ROUTES.products.root);
    },
    [router],
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobile, isMenuOpen]);

  useEffect(() => {
    if (industry) {
      const validFamiliesSlugs = filters.families.map((fam) => fam.slug);
      if (family && !validFamiliesSlugs.includes(family)) {
        setFamily(validFamiliesSlugs.length > 0 ? validFamiliesSlugs[0] : "");
      }
    } else {
      setFamily("");
    }
  }, [industry, family, filters.families]);

  useEffect(() => {
    if (family) {
      const validSubfamiliesSlugs = filters.subfamilies.map((sub) => sub.slug);
      if (subfamily && !validSubfamiliesSlugs.includes(subfamily)) {
        setSubfamily(
          validSubfamiliesSlugs.length > 0 ? validSubfamiliesSlugs[0] : "",
        );
      }
    } else {
      setSubfamily("");
    }
  }, [family, subfamily, filters.subfamilies]);

  useEffect(() => {
    if (!isMobile) {
      navigateToFilters(industry, family, subfamily, brand);
    }
  }, [industry, family, subfamily, brand, isMobile, navigateToFilters]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowMenuIcon(true);
      } else {
        setShowMenuIcon(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useFilterAutoScroll(
    industry,
    industriesContainerRef,
    !isMobile || isMenuOpen,
  );
  useFilterAutoScroll(family, familiesContainerRef, !isMobile || isMenuOpen);
  useFilterAutoScroll(
    subfamily,
    subfamiliesContainerRef,
    !isMobile || isMenuOpen,
  );
  useFilterAutoScroll(brand, brandsContainerRef, !isMobile || isMenuOpen);

  const handleRestore = () => {
    setSubfamily("");
    setBrand("");
    if (!isMobile) {
      router.replace(ROUTES.products.root);
    }
  };

  const handleApplyMobile = () => {
    navigateToFilters(industry, family, subfamily, brand);
    toggleMenu();
  };

  const renderFiltersContent = () => (
    <>
      <p className="font-semibold text-gray-700">Unidades de Medición</p>
      <div className="grid grid-cols-2">
        <button
          className={`uppercase text-sm font-medium py-2 px-4 border border-black rounded-sm rounded-r-none ${
            unitType === "english"
              ? "bg-black text-white"
              : "hover:bg-black hover:text-white"
          }`}
          onClick={() => setUnitType("english")}
        >
          EE.UU
        </button>
        <button
          className={`uppercase text-sm font-medium py-2 px-4 border border-l-0 border-black rounded-sm rounded-l-none ${
            unitType === "metric"
              ? "bg-black text-white"
              : "hover:bg-black hover:text-white"
          }`}
          onClick={() => setUnitType("metric")}
        >
          Métricas
        </button>
      </div>

      {firstSegment === "productos-por-marca" ? (
        <>
          {filters.brands.length > 0 && renderBrands()}
          {filters.industries.length > 0 && renderIndustries()}
          {industry && filters.families.length > 0 && renderFamilies()}
          {family && filters.subfamilies.length > 0 && renderSubFamilies()}
        </>
      ) : (
        <>
          {filters.industries.length > 0 && renderIndustries()}
          {industry && filters.families.length > 0 && renderFamilies()}
          {family && filters.subfamilies.length > 0 && renderSubFamilies()}
          {family && filters.brands.length > 0 && renderBrands()}
        </>
      )}

      {isMobile ? (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 hover:text-white transition uppercase text-sm"
            onClick={handleRestore}
          >
            <Image
              src="/images/icons/restoreIcon.svg"
              alt="restaurar"
              width={15}
              height={15}
            />
            Restaurar
          </button>
          <button
            className="py-2 px-4 bg-black text-white uppercase text-sm font-medium rounded"
            onClick={handleApplyMobile}
          >
            Aplicar
          </button>
        </div>
      ) : (
        <button
          className="flex items-center gap-2 w-max uppercase mt-4 py-2 px-4 bg-gray-200 text-gray-700 hover:text-white transition"
          onClick={handleRestore}
        >
          <Image
            src="/images/icons/restoreIcon.svg"
            alt="restaurar"
            width={15}
            height={15}
          />
          Restaurar
        </button>
      )}
    </>
  );

  const renderIndustries = () => (
    <div className="mb-4 mt-4">
      <h3 className="font-semibold text-gray-700">Industrias</h3>
      <div
        ref={industriesContainerRef}
        className={`space-y-2 ${
          filters.industries.length > 5 ? "max-h-40 overflow-y-auto" : ""
        }`}
      >
        {filters.industries
          .filter((ind) => ind.slug !== "agricultura")
          .map((ind) => (
            <label
              key={ind.id}
              data-selected={industry === ind.slug}
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => {
                if (industry === ind.slug) return;
                setFamily("");
                setSubfamily("");
                setIndustry(ind.slug);
              }}
            >
              <input
                type="checkbox"
                checked={industry === ind.slug}
                className="hidden peer"
                readOnly
              />
              <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
              <p className="text-sm text-gray-400 truncate">{ind.title}</p>
            </label>
          ))}
      </div>
    </div>
  );

  const renderFamilies = () => (
    <div className="mb-4">
      <h3 className="font-semibold text-gray-700">Familias</h3>
      <div
        ref={familiesContainerRef}
        className={`space-y-2 ${
          filters.families.length > 5 ? "max-h-40 overflow-y-auto" : ""
        }`}
      >
        {filters.families.map((fam) => (
          <label
            key={fam.id}
            data-selected={family === fam.slug}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => {
              if (family === fam.slug) return;
              setSubfamily("");
              setFamily(fam.slug);
            }}
          >
            <input
              type="checkbox"
              checked={family === fam.slug}
              className="hidden peer"
              readOnly
            />
            <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
            <p className="text-sm text-gray-400 truncate">{fam.title}</p>
          </label>
        ))}
      </div>
    </div>
  );

  const renderSubFamilies = () => (
    <div className="mb-4">
      <h3 className="font-semibold text-gray-700">Subfamilias</h3>
      <div
        ref={subfamiliesContainerRef}
        className={`space-y-2 ${
          filters.subfamilies.length > 5 ? "max-h-40 overflow-y-auto" : ""
        }`}
      >
        {filters.subfamilies.map((sub) => (
          <label
            key={sub.id}
            data-selected={subfamily === sub.slug}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => {
              if (subfamily === sub.slug) return;
              setSubfamily(sub.slug)
            }}
          >
            <input
              type="checkbox"
              checked={subfamily === sub.slug}
              className="hidden peer"
              readOnly
            />
            <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
            <p className="text-sm text-gray-400 truncate">{sub.title}</p>
          </label>
        ))}
      </div>
    </div>
  );

  const renderBrands = () => (
    <div className="mb-4">
      <h3 className="font-semibold text-gray-700">Marcas</h3>
      <div
        ref={brandsContainerRef}
        className={`space-y-2 ${
          filters.brands.length > 5 ? "max-h-40 overflow-y-auto" : ""
        }`}
      >
        {filters.brands.map((br) => (
          <label
            key={br.id}
            data-selected={brand === br.slug}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => setBrand(br.slug)}
          >
            <input
              type="checkbox"
              checked={brand === br.slug}
              className="hidden peer"
              readOnly
            />
            <div className="w-3 h-3 border-2 border-gray-400 rounded-sm peer-checked:bg-yellow-400"></div>
            <p className="text-sm text-gray-400 truncate">{br.title}</p>
          </label>
        ))}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="relative z-50">
        {!isMenuOpen && showMenuIcon && (
          <button
            onClick={toggleMenu}
            className="fixed bottom-5 left-5 bg-yellow-500 text-white p-4 rounded-full shadow-lg"
          >
            <Image
              src="/assets/icons/filter.svg"
              alt="Filtros"
              width={20}
              height={20}
              className="h-5 w-5"
            />
          </button>
        )}

        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-end z-50"
            onClick={toggleMenu}
          >
            <div
              className="w-full bg-white p-6 rounded-t-2xl shadow-lg transform transition-transform translate-y-0 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Filtros</h3>
                <button
                  onClick={toggleMenu}
                  className="text-gray-500 hover:text-black font-bold text-2xl"
                >
                  ✕
                </button>
              </div>
              {renderFiltersContent()}
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div className="p-4 bg-white">{renderFiltersContent()}</div>;
}
